import {
  ZIN_DISPLAY_KEY_REELS_HOTEL_PAGE,
  ZIN_KEY_REELS_HOTEL_PAGE
} from "@website/accommodation-ui/constant";
import { HotelFaq } from "@website/accommodation-ui/hotel-faq";
import { HotelInboundLinks } from "@website/accommodation-ui/hotel-inbound-links";
import { HotelReserveCancellationPolicies } from "@website/accommodation-ui/hotel-reserve-cancellation-policies";
import { HotelReserveCategories } from "@website/accommodation-ui/hotel-reserve-categories";
import { HotelReserveDetails } from "@website/accommodation-ui/hotel-reserve-details";
import { HotelReserveRecommendation } from "@website/accommodation-ui/hotel-reserve-recommendation";
import { HotelReserveRoomsLayout } from "@website/accommodation-ui/hotel-reserve-rooms-layout";
import { HotelSeoContent } from "@website/accommodation-ui/hotel-seo-content";
import { HotelShortVideos } from "@website/accommodation-ui/hotel-short-videos";
import { HotelSnappPay } from "@website/accommodation-ui/hotel-snapp-pay";
import { SchemaHotelPage } from "@website/accommodation-ui/scripts";
import { Breadcrumb } from "@website/ui/breadcrumb";
import { FabMenu } from "@website/ui/fab-menu";
import { isChannel } from "@website/utils/channel/helpers";
import { Footer } from "@website/widget/footer";
import { HeaderWrapper } from "@website/widget/header-wrapper";
import { ReserveCommentsLayout } from "@website/widget/reserve-comments-layout";
import { ReserveFacilities } from "@website/widget/reserve-facilities";
import { ReserveGallery } from "@website/widget/reserve-gallery";
import { ReserveMap } from "@website/widget/reserve-map";
import { ReservePolicies } from "@website/widget/reserve-policies";
import { ReserveRate } from "@website/widget/reserve-rate";
import toString from "lodash-es/toString";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useHotelPageDataTransform } from "./index.hook";
import { HotelPagePropertiesType } from "./index.types";

const Index: NextPage<HotelPagePropertiesType> = (properties) => {
  const router = useRouter();
  const {
    isEligible,
    hotelBreadcrumbsProps,
    hotelReserveFacilitiesProps,
    hotelReserveRoomsLayoutProps,
    hotelReserveMapProps,
    gallery,
    hotelReserveDetailsProps,
    hotelReservePoliciesProps,
    hotelReserveCancellationPoliciesProps,
    hotelReserveRateProps,
    hotelReserveCommentsProps,
    hotelReserveRecommendationProps,
    hotelReserveCategoriesProps,
    hotelSeoContentProps,
    hotelSeoFAQProps,
    hotelInboundLinksProps,
    hotelSchemaProperties,
    metaData
  } = useHotelPageDataTransform(properties);
  const fabItems = useMemo(
    () => [
      { title: "تصاویر هتل", selector: "#hotel_gallery" },
      { title: "امکانات", selector: "#hotel_facilities" },
      { title: "اتاق ها", selector: "#hotel_rooms" },
      ...(hotelReserveMapProps &&
      hotelReserveMapProps?.marker?.lat !== 35.696_814 &&
      hotelReserveMapProps?.marker?.lng !== 51.349_818_6
        ? [{ title: "موقعیت", selector: "#hotel_location" }]
        : []),
      { title: "قوانین", selector: "#hotel_policies" },
      ...(hotelReserveCommentsProps
        ? [{ title: "امتیاز و نظرات", selector: "#hotel_comments" }]
        : [])
    ],
    []
  );

  return (
    <>
      <Head>
        {isChannel(["snapp-ir"]) ? (
          <title>
            {`رزرو هتل ${toString(properties?.hotelData?.title)} در ${toString(
              properties?.hotelData?.city_title
            )}- اسنپ`}
          </title>
        ) : (
          <title>{metaData.title}</title>
        )}
        <meta name="description" content={metaData.description} />
      </Head>
      <HeaderWrapper showAuth title="هتل داخلی" />
      <SchemaHotelPage {...hotelSchemaProperties} />
      <FabMenu items={fabItems} />
      <section>
        <article className="flex flex-col">
          <div className="px-0 md:px-[24px] lg:px-[40px] xl:px-[calc((100%-1200px)/2)]">
            <div className="hidden py-[22px] md:block">
              <Breadcrumb
                {...hotelBreadcrumbsProps}
                className="hidden lg:flex"
              />
            </div>
            <ReserveGallery gallery={gallery} />
            {isChannel(["jek", "android"]) && (
              <HotelShortVideos
                className={"px-6 lg:m-0 lg:hidden"}
                displayType={"REELS"}
                displayId={ZIN_DISPLAY_KEY_REELS_HOTEL_PAGE}
                zinKey={ZIN_KEY_REELS_HOTEL_PAGE}
                zinTags={`${router.query?.cityTitle}/${router.query?.hotelTitle}`}
              />
            )}
          </div>
          <div className="px-[24px] pb-[48px] lg:px-[40px] xl:px-[calc((100%-1200px)/2)]">
            <div className="flex flex-col gap-[56px]">
              <HotelReserveDetails {...hotelReserveDetailsProps} />
              <ReserveFacilities {...hotelReserveFacilitiesProps} />
              {!isChannel(["ads"]) && (
                <HotelSnappPay
                  condition={"instalmentFacility" as const}
                  isEligible={isEligible}
                />
              )}
              <HotelReserveRoomsLayout {...hotelReserveRoomsLayoutProps} />
              {hotelReserveMapProps && <ReserveMap {...hotelReserveMapProps} />}
              <ReservePolicies {...hotelReservePoliciesProps} />
              <HotelReserveCancellationPolicies
                {...hotelReserveCancellationPoliciesProps}
              />
              <ReserveRate {...hotelReserveRateProps} />
              {!isChannel(["snapp-ir"]) && (
                <ReserveCommentsLayout {...hotelReserveCommentsProps} />
              )}
              <HotelReserveRecommendation
                {...hotelReserveRecommendationProps}
              />
              {isChannel(["website", "web"]) && (
                <HotelReserveCategories {...hotelReserveCategoriesProps} />
              )}
              <HotelSeoContent {...hotelSeoContentProps} />
              {!isChannel(["snapp-ir"]) && (
                <HotelFaq {...hotelSeoFAQProps} className="m-0 p-0" />
              )}
              <HotelInboundLinks
                {...hotelInboundLinksProps}
                className="m-0 p-0"
              />
            </div>
          </div>
        </article>
      </section>
      <Footer />
    </>
  );
};

export default Index;
export { getServerSideProps } from "./index.server";
